import { useState, useRef, useEffect } from 'react';
import { Brain, Minus, Send, Sparkles, Loader2, RotateCcw, Mic, MicOff, Volume2, VolumeX, Paperclip, FileText, X as XIcon, BookOpen, Search, CheckCircle2, ImagePlus, MessageCircleMore, Copy, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTenantAIConfig } from '@/hooks/useTenantAIConfig';
import { useAuth } from '@/lib/auth';

const API = process.env.REACT_APP_BACKEND_URL;
const SESSION_STORAGE_KEY = 'valnexo_uatu_session_id';
const DRAFT_STORAGE_KEY = 'valnexo_uatu_draft';
const AUDIO_MODE_KEY = 'valnexo_uatu_audio_mode';

// 🌱 Message V1 — affiché uniquement quand Uatu ne parvient PAS à répondre
// (tool error, réponse tronquée, erreur stream/provider). Une seule source
// de vérité pour ajuster le ton facilement plus tard.
const UATU_V1_FALLBACK = "Dsl je suis la V1 de Uatu, pour être clair je suis encore très léger et inexpérimenté, mais Jérémy est en train de m'entraîner tous les jours et de me faire acquérir plein de compétences de son côté. Bientôt je reviendrai en étant ultra fonctionnel et je serai un vrai copilote avec un accès complet à toutes les données du CRM et bien plus — je pourrai tout faire pour toi.";

// Nettoie le markdown pour la synthèse vocale (retire **, [text](url) → text, etc.)
const cleanForSpeech = (md) => {
    if (!md) return '';
    return md
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')  // [text](url) → text
        .replace(/\*\*([^*]+)\*\*/g, '$1')         // **bold** → bold
        .replace(/\*([^*]+)\*/g, '$1')             // *italic* → italic
        .replace(/`([^`]+)`/g, '$1')               // `code` → code
        .replace(/^▸\s*/gm, '')                    // puces ▸
        .replace(/^-\s*/gm, '')                    // puces -
        .replace(/^#+\s*/gm, '')                   // headings
        .replace(/\n+/g, '. ')                     // newlines → pauses
        .replace(/\s+/g, ' ')
        .trim();
};

// 🌐 Contexte UI transmis à Uatu à chaque message : où l'utilisateur est.
// Uatu résout ainsi « ce dossier », « ici », « lui » sans tool call inutile.
// Retourne {current_route, current_entity_type, current_entity_id}.
const inferUiContext = (location) => {
    const pathname = location?.pathname || '';
    const out = { current_route: pathname };
    // Détection : /prospects/:id, /projets/projet/:id, /projets/espace/:id,
    //             /academy/module/:id, /dashboard/team/user/:id, /profile/:id,
    //             /demandes-officielles/:id
    const patterns = [
        [/^\/prospects\/([^/]+)$/, 'prospect'],
        [/^\/projets\/projet\/([^/]+)$/, 'project'],
        [/^\/projets\/espace\/([^/]+)$/, 'project_space'],
        [/^\/academy\/module\/([^/]+)$/, 'academy_module'],
        [/^\/dashboard\/team\/user\/([^/]+)$/, 'user'],
        [/^\/profile\/([^/]+)$/, 'user'],
        [/^\/demandes-officielles\/([^/]+)$/, 'demande_officielle'],
    ];
    for (const [rx, kind] of patterns) {
        const m = pathname.match(rx);
        if (m) {
            out.current_entity_type = kind;
            out.current_entity_id = m[1];
            return out;
        }
    }
    return out;
};

/**
 * Ligne "outil" affichée sous la bulle Uatu quand il utilise un tool.
 *   - En cours   : icône animée + label "Uatu consulte…"
 *   - Terminé (search_knowledge) : liste des docs KB consultés avec liens
 *   - Terminé (autres) : petit check ✅ discret
 * Pour la KB, chaque doc est cliquable → `/settings?tab=knowledge_base&doc=<id>`
 * si l'utilisateur est admin_full, sinon → `source_url` si dispo.
 */
function ToolTraceLine({ event, isAdminFull, navigate }) {
    const running = event.status === 'running';
    const isKb = event.name === 'search_knowledge';
    const results = event.result?.results || [];

    if (isKb && event.status === 'done' && results.length > 0) {
        return (
            <div className="flex flex-wrap items-center gap-1.5 mb-2 text-[11px] text-slate-500" data-testid="uatu-kb-trace">
                <BookOpen className="w-3 h-3 text-violet-500 shrink-0" />
                <span className="italic">Consulte&nbsp;:</span>
                {results.map((r, idx) => (
                    <button
                        key={r.id || idx}
                        type="button"
                        onClick={() => {
                            if (isAdminFull) {
                                navigate(`/settings?tab=ai_assistant&doc=${encodeURIComponent(r.id || '')}`);
                            } else if (r.source_url) {
                                window.open(r.source_url, '_blank', 'noopener,noreferrer');
                            }
                        }}
                        title={r.extrait || r.title}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-50 border border-violet-200 text-violet-700 hover:bg-violet-100 hover:border-violet-400 transition-colors max-w-[180px] truncate cursor-pointer"
                        data-testid={`uatu-kb-doc-${r.id || idx}`}
                    >
                        <FileText className="w-2.5 h-2.5 shrink-0" />
                        <span className="truncate">{r.title || 'Sans titre'}</span>
                    </button>
                ))}
            </div>
        );
    }

    // Ligne générique (en cours ou autres tools)
    return (
        <div className="flex items-center gap-1.5 mb-1.5 text-[11px] text-slate-500 italic" data-testid={`uatu-tool-${event.name}`}>
            {running ? (
                <Search className="w-3 h-3 animate-pulse text-violet-500 shrink-0" />
            ) : event.status === 'done' ? (
                <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" />
            ) : (
                <XIcon className="w-3 h-3 text-red-500 shrink-0" />
            )}
            <span>{event.label || event.name}</span>
        </div>
    );
}

/**
 * Bulle "Parler à l'IA" flottante en bas à droite.
 * - Le composant est monté globalement dans DashboardLayout → l'état survit
 *   à la navigation entre pages.
 * - Le brouillon (input) est en plus persisté dans localStorage : si l'utilisateur
 *   ferme l'onglet ou recharge la page pendant qu'il rédigeait, il retrouve son
 *   texte au rouvrir la conversation.
 * - Le bouton "Réduire" (icône Minus) ferme la fenêtre sans effacer messages ni brouillon.
 */
export default function AIChatBubble() {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState(() => {
        try { return localStorage.getItem(DRAFT_STORAGE_KEY) || ''; } catch { return ''; }
    });
    const [loading, setLoading] = useState(false);
    // 🛑 Cancellation d'un stream en cours (fév. 2026 Phase 4).
    // `abortControllerRef` contient l'AbortController de la fetch en cours.
    // `activeRequestId` est renseigné dès que le server nous renvoie `meta.request_id`.
    const abortControllerRef = useRef(null);
    const [activeRequestId, setActiveRequestId] = useState(null);
    const [sessionId, setSessionId] = useState(() => {
        try { return localStorage.getItem(SESSION_STORAGE_KEY); } catch { return null; }
    });
    const [restoring, setRestoring] = useState(false);
    // 🔗 P1.1bis — Registry de placeholders `[[LINK:xxx]]` → URLs longues.
    // Les tools retournent des placeholders courts pour éviter que Sol
    // recopie 3000+ chars d'URL dans son output. Le frontend substitue.
    const deepLinkRegistryRef = useRef({});
    const scrollRef = useRef(null);
    const tenantAI = useTenantAIConfig();
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const isAdminFull = user?.role === 'admin_full' || user?.is_admin === true;
    const recognitionRef = useRef(null);
    const [listening, setListening] = useState(false);
    const [voiceSupported, setVoiceSupported] = useState(false);
    // Index du message dont la copie vient d'être effectuée (feedback visuel 1.5s)
    const [copiedIdx, setCopiedIdx] = useState(null);
    // 🧪 Diagnostic Canary — admin_full only. Bouton dans le header pour vérifier
    // factuellement l'état du mécanisme Canary GPT-5.6 Sol (env var, allowlist,
    // is_canary_user, effective_model). Utilise l'auth existante (localStorage).
    const [canaryDiag, setCanaryDiag] = useState(null); // null | 'loading' | {json} | {error}

    // 🔥 Pré-chauffe le snapshot dès que la bulle s'ouvre — le backend construit
    // le snapshot en tâche de fond (fire-and-forget) et le met en cache 90 s.
    // Résultat : le 1er message envoyé sera aussi rapide que les suivants.
    useEffect(() => {
        if (!open || !user?.id) return;
        const token = localStorage.getItem('token') || localStorage.getItem('valnexo_token');
        if (!token) return;
        fetch(`${API}/api/ai/chat/prewarm`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        }).catch(() => { /* silencieux — c'est du pre-warm optionnel */ });
    }, [open, user?.id]);

    // Copie le contenu markdown d'un message dans le presse-papiers.
    // On copie le markdown source (pas le HTML rendu) pour rester utile ailleurs.
    const copyMessage = async (content, idx) => {
        try {
            const clean = String(content || '')
                .replace(/^▸\s*/gm, '- ')  // puces markdown standard
                .trim();
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(clean);
            } else {
                // Fallback pour environnements non-secure (rare)
                const ta = document.createElement('textarea');
                ta.value = clean;
                ta.style.position = 'fixed';
                ta.style.opacity = '0';
                document.body.appendChild(ta);
                ta.select();
                document.execCommand('copy');
                document.body.removeChild(ta);
            }
            setCopiedIdx(idx);
            setTimeout(() => setCopiedIdx((v) => (v === idx ? null : v)), 1500);
        } catch (e) {
            toast.error('Impossible de copier');
        }
    };

    // Audio mode : Uatu lit ses réponses à voix haute
    const [audioMode, setAudioMode] = useState(() => {
        try { return localStorage.getItem(AUDIO_MODE_KEY) === '1'; } catch { return false; }
    });
    const [speaking, setSpeaking] = useState(false);
    const utteranceRef = useRef(null);
    const ttsSupported = typeof window !== 'undefined' && 'speechSynthesis' in window;

    // Détection écran tactile (mobile / tablette) — sur ces devices la touche
    // "Entrée" du clavier virtuel doit insérer un retour ligne, PAS envoyer.
    // Sinon impossible d'écrire un message sur plusieurs lignes en mobilité.
    const isTouchDevice = typeof window !== 'undefined' && (
        (window.matchMedia && window.matchMedia('(hover: none)').matches)
        || ('ontouchstart' in window)
    );

    // Pendant que le chat est ouvert on cache le FAB hamburger mobile (qui
    // s'affiche dans le même coin) — sinon il chevauche notre bouton Envoyer.
    // On broadcaste via une classe sur <body> lue par MobileNav.
    useEffect(() => {
        if (open) {
            document.body.classList.add('uatu-chat-open');
        } else {
            document.body.classList.remove('uatu-chat-open');
        }
        return () => document.body.classList.remove('uatu-chat-open');
    }, [open]);

    // ================================================================
    // Sollicitations proactives d'Uatu (peek preview + badge non-lus)
    // ================================================================
    // Uatu alimente `/api/uatu/observations`. Quand le chat est fermé :
    //  • un badge rouge sur le bouton indique le nb d'observations non-vues
    //  • une "bulle-message" (peek) affiche le dernier message important
    //    au-dessus du bouton, invitant à cliquer pour parler à Uatu.
    // À l'ouverture du chat, tout est marqué "seen" côté back.
    const [proactiveObs, setProactiveObs] = useState([]);
    const [peekDismissedIds, setPeekDismissedIds] = useState(() => {
        try { return new Set(JSON.parse(sessionStorage.getItem('valnexo_uatu_peek_dismissed') || '[]')); } catch { return new Set(); }
    });
    // Ajustement UX #2 — cooldown après dismiss d'un peek (30 min) : évite le harcèlement
    const PEEK_COOLDOWN_MS = 30 * 60 * 1000;
    const [peekCooldownUntil, setPeekCooldownUntil] = useState(() => {
        try { return parseInt(sessionStorage.getItem('valnexo_uatu_peek_cooldown_until') || '0', 10) || 0; } catch { return 0; }
    });
    // Ajustement UX #1 — sévérités déjà "vues au moins une fois" (jamais peek redouble sur du déjà connu)
    const [everPeekedIds, setEverPeekedIds] = useState(() => {
        try { return new Set(JSON.parse(sessionStorage.getItem('valnexo_uatu_peek_history') || '[]')); } catch { return new Set(); }
    });
    // Ajustement UX #3 — cap dur visuel : max N alertes affichées, triées par sévérité + impact
    const MAX_VISIBLE_ALERTS = 12;

    // Persiste les IDs dismissés dans la session (pas localStorage : reset à la fermeture d'onglet)
    useEffect(() => {
        try { sessionStorage.setItem('valnexo_uatu_peek_dismissed', JSON.stringify(Array.from(peekDismissedIds))); } catch { /* noop */ }
    }, [peekDismissedIds]);
    useEffect(() => {
        try { sessionStorage.setItem('valnexo_uatu_peek_history', JSON.stringify(Array.from(everPeekedIds))); } catch { /* noop */ }
    }, [everPeekedIds]);
    useEffect(() => {
        try { sessionStorage.setItem('valnexo_uatu_peek_cooldown_until', String(peekCooldownUntil)); } catch { /* noop */ }
    }, [peekCooldownUntil]);

    const fetchProactive = async () => {
        try {
            const token = localStorage.getItem('valnexo_token') || localStorage.getItem('token');
            if (!token) return;
            const res = await axios.get(`${API}/api/uatu/observations`, {
                headers: { Authorization: `Bearer ${token}` }, timeout: 8000,
            });
            const obs = res.data?.observations || [];
            // On garde TOUTES les sévérités côté onglet Alertes (dont vigilance) — le tri se fait au rendu.
            setProactiveObs(obs);
        } catch { /* silencieux */ }
    };

    // Poll uniquement quand le chat est fermé (économise les appels)
    useEffect(() => {
        if (open) return;
        fetchProactive();
        const t = setInterval(fetchProactive, 90 * 1000); // 90s
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Ordre de sévérité pour tri
    const SEV_RANK = { critical: 3, important: 2, vigilance: 1 };

    // Compteur des non-vues (utilisé pour le badge)
    const unseenObs = proactiveObs.filter((o) => !o.seen_at);
    const unseenCount = unseenObs.length;
    // Sévérité max présente pour colorer le badge intelligemment
    const maxUnseenSev = unseenObs.reduce((max, o) => {
        const r = SEV_RANK[o.severity] || 0;
        return r > max ? r : max;
    }, 0);
    const badgeColor = maxUnseenSev >= 3 ? 'bg-red-500' : maxUnseenSev >= 2 ? 'bg-orange-500' : 'bg-slate-400';

    // Peek intelligent : n'apparaît QUE si
    //  - il n'y a PAS de cooldown actif (30 min après dernier dismiss)
    //  - il existe une obs critical OU une importante JAMAIS peekée sur cette session
    //  - non-vue ET non-dismissée par l'user
    const nowMs = Date.now();
    const cooldownActive = peekCooldownUntil > nowMs;
    const peekCandidate = cooldownActive ? null : unseenObs
        .filter((o) => !peekDismissedIds.has(o.id))
        .filter((o) => o.severity === 'critical' || (o.severity === 'important' && !everPeekedIds.has(o.id)))
        .sort((a, b) => (SEV_RANK[b.severity] || 0) - (SEV_RANK[a.severity] || 0) || (b.impact_score || 0) - (a.impact_score || 0))[0] || null;
    const peekObs = peekCandidate;

    // Marque le peek dans l'historique dès qu'il s'affiche (anti re-peek du même)
    useEffect(() => {
        if (peekObs && !everPeekedIds.has(peekObs.id)) {
            setEverPeekedIds((prev) => new Set(prev).add(peekObs.id));
        }
    }, [peekObs, everPeekedIds]);

    // Marque toutes les observations affichées comme "vues" côté back (feu et oublie)
    const markAllSeen = async () => {
        if (unseenObs.length === 0) return;
        try {
            const token = localStorage.getItem('valnexo_token') || localStorage.getItem('token');
            if (!token) return;
            await Promise.allSettled(
                unseenObs.map((o) =>
                    axios.post(
                        `${API}/api/uatu/observations/${o.id}/seen`,
                        {},
                        { headers: { Authorization: `Bearer ${token}` }, timeout: 5000 },
                    ),
                ),
            );
        } catch { /* silencieux */ }
        // Optimistic : passe local en seen pour reset immédiat du badge
        setProactiveObs((prev) => prev.map((o) => ({ ...o, seen_at: o.seen_at || new Date().toISOString() })));
    };

    // Ouvre le chat depuis le bouton OU le peek → marque tout comme vu
    const openChatFromPeek = () => {
        setPeekDismissedIds((prev) => {
            const n = new Set(prev);
            proactiveObs.forEach((o) => n.add(o.id));
            return n;
        });
        markAllSeen();
        setOpenedFromPeek(true);
        setOpen(true);
    };

    // Dismiss juste le peek (le badge reste tant qu'il y a des non-vues)
    // + armement du cooldown 30 min pour éviter le harcèlement.
    const dismissPeek = () => {
        if (!peekObs) return;
        setPeekDismissedIds((prev) => new Set(prev).add(peekObs.id));
        setPeekCooldownUntil(Date.now() + PEEK_COOLDOWN_MS);
    };

    // === Onglets internes du chat : Conversation / Alertes ===
    // Alertes = liste des observations Uatu (ex-panneau UatuObservationsPanel désormais consolidé ici).
    const [activeTab, setActiveTab] = useState('conversation');
    // Track si l'ouverture vient du peek → on force le tab Alertes
    const [openedFromPeek, setOpenedFromPeek] = useState(false);

    // Auto-switch sur "alerts" UNIQUEMENT quand ouvert via peek — pour préserver
    // l'accès direct à la conversation libre depuis le bouton principal.
    useEffect(() => {
        if (open && openedFromPeek) {
            setActiveTab('alerts');
            setOpenedFromPeek(false);
        }
    }, [open, openedFromPeek]);

    // Toutes les observations (open + non expirées) — triées par sévérité + impact_score, capées à MAX_VISIBLE_ALERTS
    const allOpenObs = [...proactiveObs]
        .sort((a, b) => (SEV_RANK[b.severity] || 0) - (SEV_RANK[a.severity] || 0) || (b.impact_score || 0) - (a.impact_score || 0))
        .slice(0, MAX_VISIBLE_ALERTS);
    const hiddenExtraCount = Math.max(0, proactiveObs.length - allOpenObs.length);

    const dismissObservation = async (obs) => {
        try {
            const token = localStorage.getItem('valnexo_token') || localStorage.getItem('token');
            await axios.post(
                `${API}/api/uatu/observations/${obs.id}/dismiss`,
                {},
                { headers: { Authorization: `Bearer ${token}` }, timeout: 5000 },
            );
        } catch { /* silencieux */ }
        setProactiveObs((prev) => prev.filter((o) => o.id !== obs.id));
    };

    const markObservationActed = async (obs) => {
        try {
            const token = localStorage.getItem('valnexo_token') || localStorage.getItem('token');
            await axios.post(
                `${API}/api/uatu/observations/${obs.id}/acted`,
                {},
                { headers: { Authorization: `Bearer ${token}` }, timeout: 5000 },
            );
        } catch { /* silencieux */ }
        setProactiveObs((prev) => prev.filter((o) => o.id !== obs.id));
    };

    const openObservationAction = async (obs, action) => {
        // Marque acted d'abord, puis navigue et ferme le chat
        markObservationActed(obs);
        if (action.deep_link) {
            navigate(action.deep_link);
            setOpen(false);
        }
    };

    // "En parler avec Uatu" — Flow FLUIDE : auto-envoie un message contextualisé,
    // marque l'alerte comme traitée (elle disparaît), bascule sur Conversation.
    // Uatu répond directement avec le contexte de l'obs + une invitation à traiter la suivante.
    const talkAboutObservation = (obs) => {
        // 1) Compose un message riche pour Uatu
        const signalsText = (obs.signals || []).map((s) => `${s.label} : ${s.value}`).join(' · ');
        const remaining = proactiveObs.filter((o) => o.id !== obs.id).length;
        const nextHint = remaining > 0
            ? `\n\n[Contexte système : il me reste ${remaining} autre${remaining > 1 ? 's' : ''} alerte${remaining > 1 ? 's' : ''} à traiter après celle-ci. Termine ta réponse en me demandant si je veux enchaîner sur la suivante, OU si je préfère te poser autre chose.]`
            : `\n\n[Contexte système : c'est la dernière alerte. Termine ta réponse en m'invitant à te poser librement d'autres questions si besoin.]`;
        const message = `Aide-moi sur cette alerte que tu m'as remontée :\n\n**${obs.title}**\n${obs.message || ''}${signalsText ? `\nSignaux : ${signalsText}` : ''}\n\nQue me recommandes-tu concrètement ?${nextHint}`;

        // Version courte affichée dans la bulle user (le prompt technique reste envoyé à Uatu)
        const displayText = `💬 En parler : ${obs.title}`;

        // Marque l'obs comme acted (retirée de la liste immédiatement)
        markObservationActed(obs);

        // Bascule sur Conversation
        setActiveTab('conversation');

        // Envoie tout de suite le message à Uatu (avec displayText propre)
        setTimeout(() => sendMessage(message, { displayText }), 60);
    };

    // Pièces jointes : PDF/TXT/MD/CSV — le contenu est extrait côté back puis
    // prépendé au prochain message pour donner du contexte à Uatu.
    const [attachments, setAttachments] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef(null);

    const uploadFile = async (file) => {
        if (!file) return;
        // Sanity check côté front (le back re-checke)
        const MAX_MB = 8;
        if (file.size > MAX_MB * 1024 * 1024) {
            toast.error(`Fichier trop lourd (max ${MAX_MB} Mo).`);
            return;
        }
        setUploading(true);
        try {
            const token = localStorage.getItem('token') || localStorage.getItem('valnexo_token');
            const fd = new FormData();
            fd.append('file', file);
            const res = await axios.post(`${API}/api/ai/chat/extract-text`, fd, {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 30000,
            });
            const data = res.data || {};
            if (!data.extract) {
                toast.error(`Aucun texte extrait de « ${file.name} ».`);
                return;
            }
            setAttachments((prev) => [...prev, {
                filename: data.filename || file.name,
                source_type: data.source_type,
                chars: data.chars,
                truncated: data.truncated,
                extract: data.extract,
            }]);
            toast.success(`« ${data.filename} » attaché (${data.chars} caractères).`);
        } catch (e) {
            const detail = e?.response?.data?.detail || e.message || 'Erreur';
            toast.error(`Attachement refusé — ${detail}`);
        } finally {
            setUploading(false);
        }
    };

    const removeAttachment = (idx) => setAttachments((prev) => prev.filter((_, i) => i !== idx));

    // === Images Uatu — mémoire session (fév. 2026) ===
    // Upload via drag-drop, sélecteur, ou coller (Ctrl+V) d'un PNG/JPG/WEBP.
    // Stockage backend en base64 (rotation auto 3 par session). Claude Vision
    // les relira à chaque tour tant qu'elles sont attachées à la session.
    const [sessionImages, setSessionImages] = useState([]);
    const [imageUploading, setImageUploading] = useState(false);
    const imageInputRef = useRef(null);
    const IMG_MIMES = ['image/png', 'image/jpeg', 'image/webp'];

    const loadSessionImages = async () => {
        if (!sessionId) return;
        try {
            const token = localStorage.getItem('token') || localStorage.getItem('valnexo_token');
            const res = await axios.get(`${API}/api/ai/chat/session-images/${sessionId}`, {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 10000,
            });
            setSessionImages(res.data?.images || []);
        } catch { /* silencieux */ }
    };

    const uploadImage = async (file) => {
        if (!file) return;
        if (!IMG_MIMES.includes(file.type)) {
            toast.error(`Format image non supporté (${file.type}). Utilise PNG, JPEG ou WEBP.`);
            return;
        }
        if (file.size > 8 * 1024 * 1024) {
            toast.error('Image trop lourde (max 8 Mo).');
            return;
        }
        if (sessionImages.length >= 3) {
            toast.info('3 images max par session — la plus ancienne sera éjectée.');
        }
        setImageUploading(true);
        try {
            const token = localStorage.getItem('token') || localStorage.getItem('valnexo_token');
            const fd = new FormData();
            fd.append('file', file);
            const res = await axios.post(
                `${API}/api/ai/chat/upload-image?session_id=${encodeURIComponent(sessionId)}`,
                fd,
                { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 },
            );
            const data = res.data || {};
            toast.success(`Image « ${data.filename} » attachée (${data.size_kb} Ko). Uatu la lira à ton prochain message.`);
            await loadSessionImages();
        } catch (e) {
            const detail = e?.response?.data?.detail || e.message || 'Erreur';
            toast.error(`Upload image refusé — ${detail}`);
        } finally {
            setImageUploading(false);
        }
    };

    const removeSessionImage = async (imageId) => {
        try {
            const token = localStorage.getItem('token') || localStorage.getItem('valnexo_token');
            await axios.delete(`${API}/api/ai/chat/session-images/${imageId}`, {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 10000,
            });
            setSessionImages((prev) => prev.filter((i) => i.id !== imageId));
            toast.success('Image retirée.');
        } catch {
            toast.error('Impossible de retirer l\'image.');
        }
    };

    // Charge les images actives à l'ouverture du chat
    useEffect(() => {
        if (open && sessionId) loadSessionImages();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, sessionId]);

    // Coller une image (Ctrl+V / ⌘+V) → upload direct
    useEffect(() => {
        if (!open) return;
        const onPaste = (e) => {
            const items = e.clipboardData?.items || [];
            for (const it of items) {
                if (it.kind === 'file' && IMG_MIMES.includes(it.type)) {
                    const f = it.getAsFile();
                    if (f) {
                        e.preventDefault();
                        uploadImage(f);
                    }
                    break;
                }
            }
        };
        window.addEventListener('paste', onPaste);
        return () => window.removeEventListener('paste', onPaste);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, sessionId]);

    useEffect(() => {
        try {
            if (audioMode) localStorage.setItem(AUDIO_MODE_KEY, '1');
            else localStorage.removeItem(AUDIO_MODE_KEY);
        } catch { /* noop */ }
    }, [audioMode]);

    const stopSpeaking = () => {
        try { window.speechSynthesis.cancel(); } catch { /* noop */ }
        setSpeaking(false);
    };

    const speak = (text) => {
        if (!ttsSupported || !text) return;
        try {
            window.speechSynthesis.cancel();
            const u = new SpeechSynthesisUtterance(cleanForSpeech(text));
            u.lang = 'fr-FR';
            u.rate = 1.05;
            u.pitch = 1;
            // Choisit une voix française si dispo
            const voices = window.speechSynthesis.getVoices();
            const frVoice = voices.find((v) => v.lang && v.lang.startsWith('fr'));
            if (frVoice) u.voice = frVoice;
            u.onend = () => setSpeaking(false);
            u.onerror = () => setSpeaking(false);
            utteranceRef.current = u;
            setSpeaking(true);
            window.speechSynthesis.speak(u);
        } catch { setSpeaking(false); }
    };

    // Coupe la voix quand la fenêtre se ferme
    useEffect(() => {
        if (!open) stopSpeaking();
    }, [open]);

    useEffect(() => {
        const SR = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);        if (SR) {
            setVoiceSupported(true);
            const recognition = new SR();
            recognition.lang = 'fr-FR';
            recognition.continuous = false;
            recognition.interimResults = true;
            let finalTranscript = '';
            recognition.onresult = (event) => {
                let interim = '';
                for (let i = event.resultIndex; i < event.results.length; i++) {
                    const t = event.results[i][0].transcript;
                    if (event.results[i].isFinal) finalTranscript += t;
                    else interim += t;
                }
                setInput((prev) => {
                    // Remplace la portion "en cours" par l'interim, garde le final
                    return (finalTranscript + interim).trimStart();
                });
            };
            recognition.onerror = (e) => {
                setListening(false);
                if (e.error !== 'no-speech' && e.error !== 'aborted') {
                    toast.error(`Erreur voix : ${e.error}`);
                }
            };
            recognition.onend = () => setListening(false);
            recognitionRef.current = recognition;
        }
    }, []);

    // Écoute les événements externes pour ouvrir le chat avec un message pré-rempli
    // (utilisé par le banner Welcome ou d'autres triggers)
    useEffect(() => {
        const handler = (e) => {
            const msg = e?.detail?.message;
            const autoSend = e?.detail?.autoSend;
            if (msg) {
                setOpen(true);
                if (autoSend) {
                    setTimeout(() => sendMessage(msg), 300);
                } else {
                    setInput(msg);
                }
            }
        };
        window.addEventListener('uatu:open-with-message', handler);
        return () => window.removeEventListener('uatu:open-with-message', handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Écoute "uatu:open-alerts" — ouvre directement sur l'onglet Alertes
    // (utilisé par le rappel discret dans UatuGreetingBubble + la cloche notif du header)
    useEffect(() => {
        const handler = () => {
            markAllSeen();
            setOpenedFromPeek(true); // force le switch sur Alertes
            setOpen(true);
        };
        window.addEventListener('uatu:open-alerts', handler);
        return () => window.removeEventListener('uatu:open-alerts', handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const toggleVoice = () => {
        const rec = recognitionRef.current;
        if (!rec) return;
        if (listening) {
            rec.stop();
            setListening(false);
        } else {
            try {
                rec.start();
                setListening(true);
            } catch (e) {
                // ignore InvalidStateError si déjà démarré
            }
        }
    };

    // Recharge l'historique quand on ouvre la bulle si on a un session_id en mémoire
    useEffect(() => {
        if (!open || !sessionId || messages.length > 0) return;
        (async () => {
            setRestoring(true);
            try {
                const token = localStorage.getItem('token') || localStorage.getItem('valnexo_token');
                const res = await axios.get(`${API}/api/ai/chat/history/${sessionId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const msgs = (res.data?.messages || []).map((m) => ({
                    role: m.role,
                    content: m.content,
                    ts: new Date(m.created_at).getTime(),
                }));
                if (msgs.length > 0) setMessages(msgs);
            } catch { /* noop — on part d'une conversation vide */ }
            finally { setRestoring(false); }
        })();
    }, [open, sessionId, messages.length]);

    // Persiste le session_id
    useEffect(() => {
        try {
            if (sessionId) localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
        } catch { /* noop */ }
    }, [sessionId]);

    // Persiste le brouillon (draft) — l'utilisateur peut réduire la fenêtre,
    // naviguer, voire recharger la page : son texte est conservé jusqu'à envoi.
    useEffect(() => {
        try {
            if (input) localStorage.setItem(DRAFT_STORAGE_KEY, input);
            else localStorage.removeItem(DRAFT_STORAGE_KEY);
        } catch { /* noop */ }
    }, [input]);

    // Scroll auto vers le bas quand nouveau message
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, loading]);

    // Scroll vers le bas à l'ouverture du chat + au switch d'onglet Conversation
    // (l'user doit toujours voir le dernier message, pas le début du fil).
    useEffect(() => {
        if (!open || activeTab !== 'conversation') return;
        // Attend le rendu DOM (restauration historique éventuelle)
        const t = setTimeout(() => {
            if (scrollRef.current) {
                scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
            }
        }, 100);
        return () => clearTimeout(t);
    }, [open, activeTab, restoring]);

    const suggestedPrompts = [
        'Résume-moi mes priorités du jour',
        'Quels dossiers je dois relancer ?',
        "Quel est l'état de mes tâches en retard ?",
    ];

    // 🛑 Stop — annule le stream Uatu en cours (P0 utilisateur fév. 2026)
    const handleStop = async () => {
        // 1) Signal côté backend : marque le cancel_event → arrêt propre
        //    (les tools déjà commit restent, seuls les prochains cycles sont coupés).
        if (activeRequestId) {
            const token = localStorage.getItem('token') || localStorage.getItem('valnexo_token');
            try {
                await fetch(`${API}/api/ai/chat/cancel`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ request_id: activeRequestId }),
                });
            } catch { /* silencieux — l'abort côté client suffira */ }
        }
        // 2) AbortController côté fetch — coupe le stream immédiatement
        try { abortControllerRef.current?.abort(); } catch { /* noop */ }
    };

    const sendMessage = async (text, opts = {}) => {
        const trimmed = (text ?? input).trim();
        // On peut envoyer un message vide s'il y a une PJ (Uatu reçoit le contenu)
        if ((!trimmed && attachments.length === 0 && sessionImages.length === 0) || loading) return;
        // Fix 22/02/2026 : garde-fou contre l'envoi pendant un upload — les
        // images en cours d'écriture Mongo ne seraient pas vues par Claude au
        // moment du stream. On force à attendre.
        if (imageUploading || uploading) {
            toast.info(imageUploading ? 'Ton image est en train d\'être uploadée…' : 'Ton fichier est en train d\'être uploadé…');
            return;
        }

        // Prépend le texte extrait des PJ (le fichier reste visible côté user)
        let composedMessage = trimmed;
        if (attachments.length > 0) {
            const attBlocks = attachments.map((a) => {
                const preview = a.extract || '';
                return `[Fichier « ${a.filename} » — ${a.chars} caractères${a.truncated ? ' (tronqué à 15000)' : ''}]\n${preview}`;
            }).join('\n\n---\n\n');
            composedMessage = attBlocks + (trimmed ? `\n\n---\n\nQuestion : ${trimmed}` : '\n\n---\n\nQuestion : Analyse ces fichiers et donne-moi les points clés.');
        }

        // Snapshot des images actives → elles vont "monter" dans la bulle
        // utilisateur. Côté serveur, on les marque comme consommées pour qu'elles
        // disparaissent du composeur (mais restent en mémoire Vision d'Uatu).
        const attachedImages = sessionImages.map((img) => ({
            id: img.id,
            filename: img.filename,
            mime: img.mime,
            data_url: img.image_base64 ? `data:${img.mime};base64,${img.image_base64}` : null,
        }));

        // Affiche côté user un message léger (juste la question + noms des PJ, pas le contenu brut).
        // opts.displayText permet à un appel programmatique (ex: talkAboutObservation)
        // d'afficher un texte propre différent du prompt technique envoyé à Uatu.
        const displayContent = opts.displayText != null
            ? opts.displayText
            : (attachments.length > 0
                ? `${trimmed || '(analyse des fichiers joints)'}${attachments.map((a) => `\n📎 ${a.filename}`).join('')}`
                : trimmed);
        const userMsg = {
            role: 'user',
            content: displayContent,
            ts: Date.now(),
            images: attachedImages.length > 0 ? attachedImages : undefined,
        };
        setMessages((m) => [...m, userMsg]);
        setInput('');
        setAttachments([]);

        // Consomme les images côté serveur (elles ne reviendront plus dans le composeur)
        if (attachedImages.length > 0) {
            const token = localStorage.getItem('token') || localStorage.getItem('valnexo_token');
            const ids = attachedImages.map((i) => i.id);
            axios.post(
                `${API}/api/ai/chat/session-images/consume`,
                { ids },
                { headers: { Authorization: `Bearer ${token}` }, timeout: 5000 },
            ).catch(() => { /* silencieux */ });
            setSessionImages([]);
        }
        try { localStorage.removeItem(DRAFT_STORAGE_KEY); } catch { /* noop */ }
        setLoading(true);

        // 🛑 AbortController pour permettre à l'utilisateur d'appuyer sur Stop
        // pendant le stream. On expose l'objet via useRef pour que le bouton
        // Stop y accède directement.
        const abortCtrl = new AbortController();
        abortControllerRef.current = abortCtrl;
        setActiveRequestId(null); // sera renseigné par l'event meta

        // Ajoute un placeholder assistant qu'on va remplir au fil du stream
        const asstIdx = messages.length + 1; // index où va se placer la réponse
        setMessages((m) => [...m, { role: 'assistant', content: '', ts: Date.now(), streaming: true }]);

        try {
            const token = localStorage.getItem('token') || localStorage.getItem('valnexo_token');
            // Injecte le contexte UI (où l'utilisateur est) pour qu'Uatu
            // résolve « ce dossier » / « ici » / « lui » sans tool call inutile.
            const uiCtx = inferUiContext(location);
            const res = await fetch(`${API}/api/ai/chat/stream`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                signal: abortCtrl.signal,
                body: JSON.stringify({
                    message: composedMessage,
                    session_id: sessionId,
                    ...uiCtx,
                }),
            });
            if (!res.ok || !res.body) {
                throw new Error(`HTTP ${res.status}`);
            }
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let fullText = '';

            const applyChunk = (chunk) => {
                fullText += chunk;
                setMessages((prev) => {
                    const copy = [...prev];
                    if (copy[asstIdx]) {
                        // Dès que le LLM commence à produire du contenu réel,
                        // on retire le thinkingText (« Je croise les infos… »)
                        // — la réponse le remplace naturellement.
                        copy[asstIdx] = { ...copy[asstIdx], content: fullText, thinkingText: null };
                    }
                    return copy;
                });
            };

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const events = buffer.split('\n\n');
                buffer = events.pop() || '';
                for (const ev of events) {
                    const line = ev.trim();
                    if (!line.startsWith('data:')) continue;
                    try {
                        const payload = JSON.parse(line.slice(5).trim());
                        if (payload.type === 'meta' && payload.session_id && !sessionId) {
                            setSessionId(payload.session_id);
                        }
                        if (payload.type === 'meta' && payload.request_id) {
                            setActiveRequestId(payload.request_id);
                        }
                        if (payload.type === 'cancelled') {
                            // Marque la bulle comme interrompue
                            setMessages((prev) => {
                                const copy = [...prev];
                                if (copy[asstIdx]) {
                                    copy[asstIdx] = {
                                        ...copy[asstIdx],
                                        thinkingText: null,
                                        cancelled: true,
                                        cancelReason: payload.text || 'Interrompu',
                                    };
                                }
                                return copy;
                            });
                        } else if (payload.type === 'intent_resolved') {
                            // Signal métadata — on peut logger, mais on n'affiche rien.
                            // (Phase 2 : afficher un pictogramme du tier.)
                        } else if (payload.type === 'ack') {
                            // 🚨 Réaction immédiate d'Uatu AVANT tout appel LLM.
                            // Affichée en italique dans la bulle (pas mise dans fullText
                            // final pour éviter de la persister dans l'historique).
                            setMessages((prev) => {
                                const copy = [...prev];
                                if (copy[asstIdx]) {
                                    copy[asstIdx] = {
                                        ...copy[asstIdx],
                                        ackText: payload.text || '',
                                    };
                                }
                                return copy;
                            });
                        } else if (payload.type === 'thinking' || payload.type === 'progress') {
                            // 🧠 Uatu est en train de croiser des infos ou un tool
                            // dépasse 1.5 s. Affiche un statut dynamique (« Je croise
                            // les infos… », « Je poursuis… ») pour que l'utilisateur
                            // sache qu'Uatu travaille toujours — priorité P0 utilisateur.
                            setMessages((prev) => {
                                const copy = [...prev];
                                if (copy[asstIdx]) {
                                    copy[asstIdx] = {
                                        ...copy[asstIdx],
                                        thinkingText: payload.text || '',
                                        thinkingElapsed: payload.elapsed_ms || null,
                                    };
                                }
                                return copy;
                            });
                        } else if (payload.type === 'delta') {
                            applyChunk(payload.content || '');
                        } else if (payload.type === 'tool_call') {
                            // Uatu invoque un outil (ex : propose_doctrine_update)
                            setMessages((prev) => {
                                const copy = [...prev];
                                if (copy[asstIdx]) {
                                    const events = copy[asstIdx].toolEvents || [];
                                    copy[asstIdx] = {
                                        ...copy[asstIdx],
                                        toolEvents: [...events, { name: payload.name, label: payload.label, status: 'running', args: payload.arguments }],
                                    };
                                }
                                return copy;
                            });
                        } else if (payload.type === 'tool_result') {
                            const ok = payload.result?.success === true;
                            setMessages((prev) => {
                                const copy = [...prev];
                                if (copy[asstIdx]) {
                                    const events = (copy[asstIdx].toolEvents || []).map((e, idx, arr) =>
                                        idx === arr.length - 1 ? { ...e, status: ok ? 'done' : 'error', result: payload.result } : e
                                    );
                                    copy[asstIdx] = { ...copy[asstIdx], toolEvents: events };
                                }
                                return copy;
                            });
                            if (ok && payload.name === 'propose_doctrine_update') {
                                // Notifie l'app que la config a changé — force un refresh du cache tenant
                                try { window.dispatchEvent(new CustomEvent('uatu:config-updated')); } catch { /* noop */ }
                            }
                        } else if (payload.type === 'error') {
                            throw new Error(payload.detail || 'Erreur IA');
                        } else if (payload.type === 'done') {
                            // 🧪 CANARY — Capture le modèle réellement utilisé + reasoning.
                            // Affiché uniquement pour les admin_full (indicateur discret).
                            setMessages((prev) => {
                                const copy = [...prev];
                                if (copy[asstIdx]) {
                                    copy[asstIdx] = {
                                        ...copy[asstIdx],
                                        modelUsed: payload.model || null,
                                        canaryActive: payload.canary_active === true,
                                        reasoningEffort: payload.reasoning_effort || null,
                                        totalMs: payload.total_ms || null,
                                        ttftMs: payload.ttft_ms || null,
                                        costUsd: payload.cost_usd || null,
                                    };
                                }
                                return copy;
                            });
                        } else if (payload.type === 'deeplink_registry') {
                            // 🔗 P1.1bis — Le backend nous envoie la table de
                            // correspondance placeholders → URLs longues. On accumule.
                            if (payload.registry && typeof payload.registry === 'object') {
                                Object.assign(deepLinkRegistryRef.current, payload.registry);
                            }
                        } else if (payload.type === 'understood' || payload.type === 'microstate') {
                            // 💡 Latence perçue — event contextuel (reformulation d'intent
                            // ou état intermédiaire basé sur les vrais chiffres tool).
                            setMessages((prev) => {
                                const copy = [...prev];
                                if (copy[asstIdx]) {
                                    const list = copy[asstIdx].perceivedStates || [];
                                    copy[asstIdx] = {
                                        ...copy[asstIdx],
                                        perceivedStates: [...list, { kind: payload.type, text: payload.text }],
                                    };
                                }
                                return copy;
                            });
                        }
                    } catch (e) {
                        // ignore parse errors on partial buffer
                    }
                }
            }

            // Finalise (retire le flag streaming) + lit à voix haute si audio mode
            // Fix 22/02/2026 (prod) : si le stream s'est terminé SANS aucun texte
            // ET sans erreur, on synthétise un message depuis les tool_results
            // pour ne PAS laisser une bulle vide (bug reporté par Jérémy).
            setMessages((prev) => {
                const copy = [...prev];
                if (copy[asstIdx]) {
                    const cur = copy[asstIdx];
                    let finalContent = cur.content;
                    if (!finalContent || !finalContent.trim()) {
                        const toolEvents = cur.toolEvents || [];
                        const successMsgs = toolEvents
                            .filter((e) => e.status === 'done' && e.result && (e.result.message || e.result.note))
                            .map((e) => e.result.message || e.result.note);
                        const errorMsgs = toolEvents
                            .filter((e) => e.status === 'error')
                            .map((e) => e.result?.error || 'Erreur outil');
                        if (successMsgs.length > 0) {
                            finalContent = successMsgs.join('\n\n');
                        } else if (errorMsgs.length > 0) {
                            // 🌱 V1 fallback — on log le détail technique pour debug
                            // mais on affiche le message d'attente V1 à l'utilisateur.
                            try { console.debug('[Uatu V1] tool errors:', errorMsgs); } catch { /* noop */ }
                            finalContent = UATU_V1_FALLBACK;
                        } else if (toolEvents.length > 0) {
                            finalContent = '_(Uatu a fini son analyse — pose une question pour aller plus loin)_';
                        } else {
                            // 🌱 V1 fallback — réponse tronquée / silencieuse.
                            finalContent = UATU_V1_FALLBACK;
                        }
                    }
                    copy[asstIdx] = { ...cur, content: finalContent, streaming: false };
                    // Lit la réponse finale si audio mode activé
                    if (audioMode && ttsSupported && finalContent) {
                        setTimeout(() => speak(finalContent), 100);
                    }
                }
                return copy;
            });
        } catch (e) {
            // 🛑 AbortError = l'utilisateur a cliqué Stop → pas une erreur
            if (e?.name === 'AbortError') {
                setMessages((prev) => {
                    const copy = [...prev];
                    if (copy[asstIdx]) {
                        const cur = copy[asstIdx];
                        copy[asstIdx] = {
                            ...cur,
                            streaming: false,
                            cancelled: true,
                            cancelReason: cur.cancelReason || 'Mission interrompue',
                            content: cur.content || '_(Mission interrompue par l\'utilisateur)_',
                        };
                    }
                    return copy;
                });
            } else {
                // 🌱 V1 fallback — erreur stream/provider (timeout, 502, 503,
                // erreur explicite `type: 'error'`). On loggue en console pour
                // debug, pas de toast d'alarme rouge — le message dans la bulle
                // suffit et reste doux pour l'utilisateur.
                try { console.debug('[Uatu V1] stream/provider error:', e); } catch { /* noop */ }
                setMessages((prev) => {
                    const copy = [...prev];
                    if (copy[asstIdx]) copy[asstIdx] = { role: 'assistant', content: UATU_V1_FALLBACK, ts: Date.now() };
                    return copy;
                });
            }
        } finally {
            setLoading(false);
            setActiveRequestId(null);
            abortControllerRef.current = null;
        }
    };

    const onKeyDown = (e) => {
        // Sur mobile / tactile : Entrée = retour ligne (comportement natif iMessage /
        // WhatsApp). L'envoi se fait UNIQUEMENT via le bouton Envoyer. Sinon on ne
        // peut pas écrire un message sur plusieurs lignes en mobilité.
        // Sur desktop : Entrée = envoyer, Shift+Entrée = retour ligne.
        if (isTouchDevice) return;
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            // Fix 22/02/2026 : bloquer l'envoi tant qu'une image est en cours
            // d'upload — sinon la PJ ne part pas car pas encore persistée en
            // session Mongo au moment où le stream Claude démarre.
            if (imageUploading || uploading) {
                toast.info(imageUploading ? 'Attends la fin de l\'upload de ton image…' : 'Attends la fin de l\'upload du fichier…');
                return;
            }
            sendMessage();
        }
    };

    // Rendu très léger : bold **texte**, listes à puces ▸/-, retours ligne.
    const renderContent = (text) => {
        // Rend en HTML sécurisé : gras + liens markdown [texte](url) cliquables.
        // Brief fév. 2026 : Uatu propose des deep-links, ils DOIVENT être cliquables.
        // Politique URL : on autorise http(s):// et les liens relatifs commençant par / ;
        // tout le reste est neutralisé pour éviter les XSS (`javascript:`, `data:`, etc.).
        // 🩹 Normalisation legacy (fév 2026) : Uatu peut historiquement produire
        //    /hub/projects/<id> ou /hub/... — on redirige vers les vraies routes
        //    /projets/projet/<id> côté client, évitant un catch-all `*` → /dashboard.
        const normalizePath = (u) => {
            if (!u || typeof u !== 'string') return u;
            // Legacy hub → projets
            const m1 = u.match(/^\/hub\/projects?\/([^/?#]+)/i);
            if (m1) return u.replace(/^\/hub\/projects?\/[^/?#]+/i, `/projets/projet/${m1[1]}`);
            const m2 = u.match(/^\/hub\/space\/([^/?#]+)/i);
            if (m2) return u.replace(/^\/hub\/space\/[^/?#]+/i, `/projets/espace/${m2[1]}`);
            if (u === '/hub' || u.startsWith('/hub?')) return '/projets' + u.slice(4);
            return u;
        };
        const safeUrl = (raw) => {
            const u = normalizePath(String(raw || '').trim());
            // P1.1bis — placeholder non substitué (registry pas encore arrivé)
            // → refuser plutôt que naviguer vers /[[LINK:xxx]] 404
            if (u.includes('[[LINK:')) return '#';
            if (/^https?:\/\//i.test(u)) return u;
            if (u.startsWith('/')) return u;
            return '#';
        };
        const escape = (s) => String(s || '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
        const renderMarkdown = (ln) => {
            // 🔗 P1.1bis — Substitue les placeholders `[[LINK:xxx]]` par la vraie
            // URL du registry (envoyée via SSE `deeplink_registry`). Sol utilise
            // ces placeholders courts pour éviter de recopier 3000+ chars et
            // dépasser son cap max_tokens en plein milieu de l'URL.
            const registry = deepLinkRegistryRef.current || {};
            const substituted = String(ln || '').replace(/\[\[LINK:([a-zA-Z0-9_\-]+)\]\]/g, (match, key) => {
                return registry[key] || match;
            });
            ln = substituted;
            // 1) On extrait d'abord les liens markdown en tokens pour ne pas
            //    laisser le texte du lien passer par le remplaceur `**bold**` qui
            //    casserait la structure.
            const parts = [];
            const linkRegex = /\[([^\]]+)\]\(([^)\s]+)\)/g;
            let lastIdx = 0;
            let m;
            while ((m = linkRegex.exec(ln)) !== null) {
                if (m.index > lastIdx) parts.push({ type: 'text', value: ln.slice(lastIdx, m.index) });
                parts.push({ type: 'link', label: m[1], href: m[2] });
                lastIdx = m.index + m[0].length;
            }
            if (lastIdx < ln.length) parts.push({ type: 'text', value: ln.slice(lastIdx) });
            // 2) On assemble en HTML : le texte hors lien est échappé + rendu gras,
            //    le libellé du lien est échappé + rendu gras + wrappé en <a>.
            const boldify = (s) => escape(s).replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
            const isExternal = (u) => /^https?:\/\//i.test(u);
            return parts.map((p) => {
                if (p.type === 'text') return boldify(p.value);
                const href = safeUrl(p.href);
                const internal = !isExternal(href) && href.startsWith('/');
                const target = isExternal(href) ? ' target="_blank" rel="noopener noreferrer"' : '';
                // `uatu-inlink` = classe marker interceptée par le container
                // (click delegation) pour naviguer via React Router SANS reload.
                const cls = internal ? 'uatu-inlink text-violet-700 underline decoration-violet-300 hover:decoration-violet-600 hover:text-violet-900 font-medium' : 'text-violet-700 underline decoration-violet-300 hover:decoration-violet-600 hover:text-violet-900 font-medium';
                return `<a href="${escape(href)}"${target} class="${cls}" data-uatu-href="${escape(href)}">${boldify(p.label)}</a>`;
            }).join('');
        };
        const lines = String(text || '').split('\n');
        return lines.map((ln, i) => (
            <p key={i} className="whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: renderMarkdown(ln) || '&nbsp;' }} />
        ));
    };

    // 🖱️ Click delegation pour les liens internes rendus dans le chat.
    // Un `<a href="/projets/projet/..">` normal provoque un FULL PAGE RELOAD :
    // en SPA React Router c'est OK mais lourd, et surtout le catch-all `*`
    // ne matche RIEN de /projets → aucun problème direct — MAIS si Uatu a
    // historiquement produit un URL legacy (/hub/projects/...), le catch-all
    // renvoyait au dashboard. On intercepte tout et on route via navigate().
    const handleMessageClick = (e) => {
        const a = e.target.closest && e.target.closest('a.uatu-inlink');
        if (!a) return;
        const href = a.getAttribute('data-uatu-href') || a.getAttribute('href');
        if (!href || !href.startsWith('/')) return;
        // Ne pas intercepter si modifier keys (nouvel onglet, etc.)
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        try { navigate(href); } catch { window.location.href = href; return; }
        // Réduit la bulle pour laisser voir la page cible
        setOpen(false);
    };

    return (
        <>
            {/* Peek preview — bulle "message d'Uatu" au-dessus du bouton (quand fermé) */}
            {!open && peekObs && (
                <div
                    data-testid="uatu-peek-bubble"
                    className="fixed right-4 sm:right-6 z-40 max-w-[calc(100vw-2rem)] sm:max-w-[340px] animate-in fade-in slide-in-from-bottom-2 duration-500
                               bottom-44 sm:bottom-[160px]"
                >
                    <div className="relative bg-white rounded-2xl rounded-br-md shadow-2xl border border-slate-200 p-3.5 pr-8">
                        {/* Bouton X — dismiss le peek uniquement */}
                        <button
                            type="button"
                            onClick={dismissPeek}
                            data-testid="uatu-peek-dismiss"
                            className="absolute top-1.5 right-1.5 text-slate-300 hover:text-slate-600 transition-colors"
                            aria-label="Masquer"
                        >
                            <XIcon className="w-3.5 h-3.5" />
                        </button>

                        {/* En-tête Uatu */}
                        <div className="flex items-center gap-1.5 text-[11px] font-semibold text-purple-700 mb-1">
                            <Sparkles className="w-3 h-3" />
                            <span>{tenantAI.assistant_name}</span>
                            {unseenCount > 1 && (
                                <span className="ml-1 px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 text-[9px] font-bold">
                                    +{unseenCount - 1} autre{unseenCount - 1 > 1 ? 's' : ''}
                                </span>
                            )}
                        </div>

                        {/* Message cliquable pour ouvrir le chat */}
                        <button
                            type="button"
                            onClick={openChatFromPeek}
                            data-testid="uatu-peek-open"
                            className="text-left w-full group"
                        >
                            <p className="text-sm text-slate-900 font-medium leading-snug line-clamp-2 group-hover:text-purple-700 transition-colors">
                                {peekObs.title || 'J\'ai quelque chose à te signaler.'}
                            </p>
                            {peekObs.message && (
                                <p className="text-[12px] text-slate-500 leading-snug mt-1 line-clamp-2">
                                    {peekObs.message}
                                </p>
                            )}
                            <div className="mt-2 inline-flex items-center gap-1 text-[11px] text-purple-600 font-semibold group-hover:text-purple-800">
                                <MessageCircleMore className="w-3 h-3" />
                                <span>Parler à {tenantAI.assistant_name}</span>
                            </div>
                        </button>

                        {/* Petit "queue" façon speech-bubble */}
                        <div
                            className="absolute -bottom-1.5 right-6 w-3 h-3 bg-white border-r border-b border-slate-200 rotate-45"
                            aria-hidden="true"
                        />
                    </div>
                </div>
            )}

            <button
                type="button"
                onClick={() => { markAllSeen(); setOpen(true); }}
                data-testid="ai-chat-bubble"
                className={`fixed z-40 rounded-2xl px-4 py-3 text-white flex items-center gap-2 shadow-2xl transition-transform hover:scale-105
                           bottom-24 right-4 sm:bottom-6 sm:right-6
                           ${maxUnseenSev >= 3 ? 'uatu-pulse-critical' : ''}`}
                style={{
                    background: 'linear-gradient(135deg, #7C3AED 0%, #6366F1 100%)',
                    boxShadow: '0 12px 32px rgba(124, 58, 237, 0.35)',
                }}
                title={input ? `Brouillon en attente — clique pour reprendre` : `Parler à ${tenantAI.assistant_name}`}
            >
                <Brain className="w-5 h-5" />
                <span className="text-sm font-semibold hidden sm:inline">Parler à {tenantAI.assistant_name}</span>
                {input ? (
                    <span className="text-[10px] px-1.5 py-0.5 bg-amber-400 text-amber-950 rounded-full font-bold animate-pulse" data-testid="ai-chat-draft-badge">brouillon</span>
                ) : (
                    <span className="text-[10px] px-1.5 py-0.5 bg-white/20 rounded-full">{tenantAI.assistant_name}</span>
                )}
                {/* Badge des non-vues d'Uatu — couleur adaptée à la sévérité MAX présente
                    (rouge = critique, orange = important, gris = vigilance seule) */}
                {unseenCount > 0 && (
                    <span
                        data-testid="uatu-unread-badge"
                        className={`absolute -top-1.5 -right-1.5 min-w-[22px] h-[22px] px-1.5 rounded-full ${badgeColor} text-white text-[11px] font-bold flex items-center justify-center shadow-lg ring-2 ring-white animate-in zoom-in duration-300`}
                        aria-label={`${unseenCount} sollicitation${unseenCount > 1 ? 's' : ''} d'Uatu`}
                    >
                        {unseenCount > 9 ? '9+' : unseenCount}
                    </span>
                )}
            </button>

            {open && (
                <div
                    className="fixed inset-0 z-[70] flex items-end sm:items-end justify-end p-0 sm:p-4 bg-slate-900/40 backdrop-blur-sm"
                    onClick={() => setOpen(false)}
                    data-testid="ai-chat-modal"
                >
                    <div
                        className="w-full sm:w-[440px] h-[92vh] sm:h-[600px] rounded-t-2xl sm:rounded-2xl bg-white shadow-2xl overflow-hidden flex flex-col animate-in slide-in-from-bottom"
                        onClick={(e) => e.stopPropagation()}
                        onDragOver={(e) => {
                            // Fix 22/02/2026 : bloquer le comportement navigateur
                            // (ouvrir le fichier dans l'onglet et casser le chat)
                            // sur TOUT le container chat, pas juste l'input area.
                            e.preventDefault();
                            e.stopPropagation();
                            if (!dragActive) setDragActive(true);
                        }}
                        onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); }}
                        onDragLeave={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            // Ne baisse le flag que si on quitte VRAIMENT le container
                            if (e.currentTarget === e.target || !e.currentTarget.contains(e.relatedTarget)) {
                                setDragActive(false);
                            }
                        }}
                        onDrop={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setDragActive(false);
                            const files = Array.from(e.dataTransfer?.files || []);
                            for (const f of files) {
                                if (IMG_MIMES.includes(f.type)) {
                                    // eslint-disable-next-line no-await-in-loop
                                    await uploadImage(f);
                                } else {
                                    // eslint-disable-next-line no-await-in-loop
                                    await uploadFile(f);
                                }
                            }
                        }}
                    >
                        {/* Header */}
                        <div
                            className="p-4 text-white flex items-center justify-between shrink-0"
                            style={{ background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 50%, #A855F7 100%)' }}
                        >
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center relative">
                                    <Brain className="w-6 h-6" />
                                    <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-white" />
                                </div>
                                <div>
                                    <p className="font-bold">{tenantAI.assistant_name}</p>
                                    <p className="text-xs text-white/80 flex items-center gap-1">
                                        <Sparkles className="w-3 h-3" /> Omniscient · le Watcher {tenantAI.brand_name}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={async () => {
                                        // Efface la session côté serveur + local, repart de zéro
                                        if (sessionId) {
                                            try {
                                                const token = localStorage.getItem('token') || localStorage.getItem('valnexo_token');
                                                await axios.delete(`${API}/api/ai/chat/session/${sessionId}`, {
                                                    headers: { Authorization: `Bearer ${token}` },
                                                });
                                            } catch { /* noop */ }
                                        }
                                        setMessages([]);
                                        setSessionId(null);
                                        try { localStorage.removeItem(SESSION_STORAGE_KEY); } catch { /* noop */ }
                                        toast.success('Nouvelle conversation démarrée');
                                    }}
                                    className="text-white/70 hover:text-white p-1"
                                    title="Nouvelle conversation"
                                    aria-label="Nouvelle conversation"
                                    data-testid="ai-chat-new-btn"
                                >
                                    <RotateCcw className="w-4 h-4" />
                                </button>
                                {isAdminFull && (
                                    <button
                                        type="button"
                                        onClick={async () => {
                                            try {
                                                const token = localStorage.getItem('valnexo_token') || localStorage.getItem('token');
                                                const res = await fetch(`${API}/api/ai/audit-export/download`, {
                                                    headers: { Authorization: `Bearer ${token}` },
                                                });
                                                if (!res.ok) {
                                                    toast.error(`Download HTTP ${res.status}`);
                                                    return;
                                                }
                                                const blob = await res.blob();
                                                const url = URL.createObjectURL(blob);
                                                const a = document.createElement('a');
                                                a.href = url;
                                                a.download = 'UNFAIR_NETWORK_FULL_AUDIT_EXPORT.tar.gz';
                                                document.body.appendChild(a);
                                                a.click();
                                                a.remove();
                                                URL.revokeObjectURL(url);
                                                toast.success(`Téléchargé (${Math.round(blob.size / 1024)} KB)`);
                                            } catch (err) {
                                                toast.error(`Erreur : ${err?.message || 'inconnue'}`);
                                            }
                                        }}
                                        className="text-white/70 hover:text-white p-1 text-[10px] font-semibold border border-white/30 rounded px-1.5"
                                        title="Télécharger l'audit complet Unfair Network (2.2MB tar.gz)"
                                        aria-label="Télécharger audit"
                                        data-testid="ai-chat-download-audit-btn"
                                    >
                                        AUDIT
                                    </button>
                                )}
                                {isAdminFull && (
                                    <button
                                        type="button"
                                        onClick={async () => {
                                            setCanaryDiag('loading');
                                            try {
                                                const token = localStorage.getItem('valnexo_token') || localStorage.getItem('token');
                                                const res = await axios.get(`${API}/api/ai/canary-status`, {
                                                    headers: { Authorization: `Bearer ${token}` },
                                                    timeout: 10000,
                                                });
                                                setCanaryDiag({ ok: true, data: res.data });
                                            } catch (err) {
                                                setCanaryDiag({ ok: false, error: err?.response?.data?.detail || err?.message || 'Erreur' });
                                            }
                                        }}
                                        className="text-white/70 hover:text-white p-1 text-[10px] font-semibold border border-white/30 rounded px-1.5"
                                        title="Diagnostic Canary (admin_full)"
                                        aria-label="Diagnostic Canary"
                                        data-testid="ai-chat-canary-diag-btn"
                                    >
                                        DIAG
                                    </button>
                                )}
                                {ttsSupported && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (speaking) stopSpeaking();
                                            setAudioMode((v) => !v);
                                        }}
                                        className={`p-1 rounded-md transition-colors ${audioMode ? 'text-emerald-300 bg-white/10' : 'text-white/70 hover:text-white'}`}
                                        aria-label={audioMode ? 'Désactiver la voix' : 'Activer la voix'}
                                        title={audioMode ? 'Voix ACTIVÉE — Uatu lit ses réponses. Clique pour couper.' : 'Voix DÉSACTIVÉE. Clique pour qu\'Uatu lise ses réponses.'}
                                        data-testid="ai-chat-audio-toggle"
                                    >
                                        {audioMode ? <Volume2 className={`w-5 h-5 ${speaking ? 'animate-pulse' : ''}`} /> : <VolumeX className="w-5 h-5" />}
                                    </button>
                                )}
                                <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white p-1" aria-label="Réduire (ta conversation reste sauvegardée)" title="Réduire — ta conversation et ton brouillon restent sauvegardés" data-testid="ai-chat-close-btn">
                                    <Minus className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        {/* Onglets Conversation / Alertes */}
                        <div className="flex items-stretch border-b border-slate-200 bg-white shrink-0" data-testid="ai-chat-tabs">
                            <button
                                type="button"
                                onClick={() => setActiveTab('conversation')}
                                data-testid="ai-chat-tab-conversation"
                                className={`flex-1 py-2.5 text-sm font-semibold transition-colors relative ${
                                    activeTab === 'conversation'
                                        ? 'text-purple-700 bg-purple-50/50'
                                        : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                                }`}
                            >
                                <span className="inline-flex items-center gap-1.5">
                                    <MessageCircleMore className="w-4 h-4" />
                                    Conversation
                                </span>
                                {activeTab === 'conversation' && (
                                    <span className="absolute bottom-0 left-4 right-4 h-0.5 bg-purple-600 rounded-full" />
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={() => setActiveTab('alerts')}
                                data-testid="ai-chat-tab-alerts"
                                className={`flex-1 py-2.5 text-sm font-semibold transition-colors relative ${
                                    activeTab === 'alerts'
                                        ? 'text-purple-700 bg-purple-50/50'
                                        : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                                }`}
                            >
                                <span className="inline-flex items-center gap-1.5">
                                    <Sparkles className="w-4 h-4" />
                                    Alertes
                                    {allOpenObs.length > 0 && (
                                        <span className={`ml-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center ${
                                            unseenCount > 0 ? 'bg-red-500 text-white' : 'bg-slate-200 text-slate-600'
                                        }`}>
                                            {allOpenObs.length > 9 ? '9+' : allOpenObs.length}
                                        </span>
                                    )}
                                </span>
                                {activeTab === 'alerts' && (
                                    <span className="absolute bottom-0 left-4 right-4 h-0.5 bg-purple-600 rounded-full" />
                                )}
                            </button>
                        </div>

                        {/* Messages */}
                        <div
                            ref={scrollRef}
                            className={`flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50 ${activeTab === 'conversation' ? '' : 'hidden'}`}
                            data-testid="ai-chat-messages"
                            onClick={handleMessageClick}
                        >
                            {restoring && (
                                <div className="flex justify-center py-2">
                                    <div className="text-xs text-slate-500 flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-full border border-slate-200">
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                        Restauration de ta conversation…
                                    </div>
                                </div>
                            )}
                            {!restoring && messages.length === 0 && (
                                <div className="space-y-3">
                                    <div className="rounded-xl bg-white border border-slate-200 p-3.5 shadow-sm">
                                        <p className="text-sm text-slate-700 leading-relaxed">
                                            👋 Bonjour, je suis <strong>{tenantAI.assistant_name}</strong>. Je connais ton périmètre CRM (dossiers, tâches, équipes) et je peux te guider.
                                        </p>
                                    </div>
                                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mt-3">Suggestions</p>
                                    <div className="space-y-1.5">
                                        {suggestedPrompts.map((p) => (
                                            <button
                                                key={p}
                                                onClick={() => sendMessage(p)}
                                                data-testid={`ai-chat-suggestion-${p.substring(0, 20).replace(/\s+/g, '-')}`}
                                                className="w-full text-left text-sm rounded-lg bg-white border border-violet-200 px-3 py-2 hover:bg-violet-50 hover:border-violet-400 transition-colors text-slate-700"
                                            >
                                                {p}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {messages.map((m, i) => {
                                // On saute la bulle assistant vide : soit streaming en cours
                                // (l'indicateur de réflexion s'affiche en dessous), soit
                                // finalisée sans contenu ET sans outils (edge case timeout).
                                // Fix 22/02/2026 (prod) : évite la bulle blanche fantôme.
                                // Note : on garde la bulle si un `ackText` est présent (réaction
                                // immédiate d'Uatu — Phase 1 conversationnelle fév. 2026).
                                if (m.role === 'assistant' && !m.content && !m.ackText && (!m.toolEvents || m.toolEvents.length === 0)) {
                                    return null;
                                }
                                return (
                                    <div
                                        key={i}
                                        className={`group/msg flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                                        data-testid={`ai-chat-msg-${m.role}`}
                                    >
                                        <div className="relative max-w-[85%]">
                                        <div
                                            className={`rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed select-text ${
                                                m.role === 'user'
                                                    ? 'bg-violet-600 text-white'
                                                    : m.error
                                                        ? 'bg-red-50 border border-red-200 text-red-800'
                                                        : 'bg-white border border-slate-200 text-slate-800 shadow-sm'
                                            }`}
                                        >
                                            {/* Trace outils Uatu : KB consultée, dossiers cherchés, etc.
                                                Affichée AU-DESSUS du contenu pour donner la transparence
                                                sur ses sources en temps réel (fév. 2026 — KB Recherche Live). */}
                                            {m.role === 'assistant' && (m.toolEvents || []).length > 0 && (
                                                <div className="mb-1">
                                                    {m.toolEvents.map((ev, ei) => (
                                                        <ToolTraceLine
                                                            key={ei}
                                                            event={ev}
                                                            isAdminFull={isAdminFull}
                                                            navigate={navigate}
                                                        />
                                                    ))}
                                                </div>
                                            )}
                                            {m.role === 'user' && Array.isArray(m.images) && m.images.length > 0 && (
                                                <div className="mb-1.5 flex flex-wrap gap-1.5" data-testid="ai-chat-msg-images">
                                                    {m.images.map((img) => (
                                                        img.data_url ? (
                                                            <a
                                                                key={img.id}
                                                                href={img.data_url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="block rounded-lg overflow-hidden border border-violet-300/50 hover:opacity-90 transition-opacity"
                                                                title={img.filename}
                                                                data-testid={`ai-chat-msg-image-${img.id}`}
                                                            >
                                                                <img
                                                                    src={img.data_url}
                                                                    alt={img.filename}
                                                                    className="max-w-[220px] max-h-[220px] object-cover block"
                                                                />
                                                            </a>
                                                        ) : (
                                                            <span
                                                                key={img.id}
                                                                className="inline-flex items-center gap-1.5 rounded-full bg-white/10 text-white border border-white/20 px-2.5 py-1 text-xs"
                                                            >
                                                                <span className="max-w-[160px] truncate">📎 {img.filename}</span>
                                                            </span>
                                                        )
                                                    ))}
                                                </div>
                                            )}
                                            {m.role === 'assistant' && m.ackText && (
                                                <div
                                                    className="text-xs italic text-slate-500 mb-1.5 border-l-2 border-violet-300 pl-2"
                                                    data-testid="uatu-ack-line"
                                                >
                                                    {m.ackText}
                                                </div>
                                            )}
                                            {m.role === 'assistant' && m.thinkingText && !m.content && (
                                                <div
                                                    className="text-xs italic text-violet-500 mb-1.5 flex items-center gap-1.5"
                                                    data-testid="uatu-thinking-line"
                                                >
                                                    <span className="inline-block w-2 h-2 rounded-full bg-violet-400 animate-pulse" />
                                                    {m.thinkingText}
                                                    {m.thinkingElapsed && (
                                                        <span className="text-slate-400">({(m.thinkingElapsed / 1000).toFixed(1)}s)</span>
                                                    )}
                                                </div>
                                            )}
                                            {m.role === 'assistant' && Array.isArray(m.perceivedStates) && m.perceivedStates.length > 0 && (
                                                <div className="mb-2 space-y-1" data-testid="uatu-perceived-states">
                                                    {m.perceivedStates.map((ps, ix) => (
                                                        <div
                                                            key={ix}
                                                            className={`text-xs italic pl-2 border-l-2 ${
                                                                ps.kind === 'understood'
                                                                    ? 'text-violet-600 border-violet-400'
                                                                    : 'text-slate-500 border-slate-300'
                                                            }`}
                                                            data-testid={`uatu-${ps.kind}-line`}
                                                        >
                                                            {ps.text}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            {renderContent(m.content)}
                                            {m.streaming && (
                                                <span className="inline-block w-1.5 h-4 bg-violet-500 ml-0.5 align-middle animate-pulse rounded-sm" />
                                            )}
                                            {/* 🧪 Indicateur modèle CANARY — admin_full uniquement.
                                                Affiche le modèle réellement utilisé côté backend,
                                                le reasoning effort et l'état canary. */}
                                            {m.role === 'assistant' && !m.streaming && isAdminFull && m.modelUsed && (
                                                <div
                                                    className={`mt-2 inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-medium border ${
                                                        m.canaryActive
                                                            ? 'bg-amber-50 border-amber-200 text-amber-700'
                                                            : 'bg-slate-50 border-slate-200 text-slate-500'
                                                    }`}
                                                    title={`Modèle : ${m.modelUsed}${m.reasoningEffort ? ' · reasoning=' + m.reasoningEffort : ''}${m.totalMs ? ' · ' + m.totalMs + 'ms' : ''}${typeof m.costUsd === 'number' ? ' · $' + m.costUsd.toFixed(4) : ''}`}
                                                    data-testid="uatu-model-indicator"
                                                >
                                                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${m.canaryActive ? 'bg-amber-500' : 'bg-slate-400'}`} />
                                                    <span>
                                                        {m.modelUsed === 'gpt-5.6-sol' ? 'GPT-5.6 Sol' : m.modelUsed}
                                                        {m.reasoningEffort ? ` · ${m.reasoningEffort}` : ''}
                                                        {m.canaryActive ? ' · Canary' : ''}
                                                    </span>
                                                    {m.totalMs && (
                                                        <span className="text-slate-400">· {(m.totalMs / 1000).toFixed(2)}s</span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        {/* Bouton Copier — visible au survol sur les messages non-vides et non en streaming.
                                            Pattern ChatGPT/Claude : discret, présent partout, feedback ✓ pendant 1.5s. */}
                                        {!m.streaming && m.content && !m.error && (
                                            <button
                                                type="button"
                                                onClick={() => copyMessage(m.content, i)}
                                                className={`absolute -bottom-2 ${m.role === 'user' ? 'left-2' : 'right-2'} opacity-0 group-hover/msg:opacity-100 focus:opacity-100 transition-opacity inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium border shadow-sm ${
                                                    copiedIdx === i
                                                        ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                                                        : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                                                }`}
                                                title="Copier le message"
                                                data-testid={`ai-chat-msg-copy-${i}`}
                                            >
                                                {copiedIdx === i ? (
                                                    <>
                                                        <Check className="w-3 h-3" />
                                                        Copié
                                                    </>
                                                ) : (
                                                    <>
                                                        <Copy className="w-3 h-3" />
                                                        Copier
                                                    </>
                                                )}
                                            </button>
                                        )}
                                        </div>
                                    </div>
                                );
                            })}

                            {loading && (() => {
                            // On affiche les points en vague quand Uatu réfléchit :
                            // aucun message en streaming, OU un message en streaming
                            // qui n'a encore aucun contenu ni outil (pur "réflexion").
                            const streamingIdx = messages.findIndex((m) => m.streaming);
                            const streamingMsg = streamingIdx >= 0 ? messages[streamingIdx] : null;
                            const stillThinking = !streamingMsg ||
                                (!streamingMsg.content && !streamingMsg.ackText && (!streamingMsg.toolEvents || streamingMsg.toolEvents.length === 0));
                            if (!stillThinking) return null;
                            return (
                                <div className="flex justify-start" data-testid="ai-chat-thinking">
                                    <div className="max-w-[85%] rounded-2xl px-4 py-3 bg-white border border-slate-200 shadow-sm flex items-center gap-3">
                                        <div className="flex items-end gap-1 h-5" aria-label="Uatu réfléchit">
                                            <span className="w-1.5 h-1.5 rounded-full bg-violet-500 uatu-wave-dot" style={{ animationDelay: '0ms' }} />
                                            <span className="w-1.5 h-1.5 rounded-full bg-violet-500 uatu-wave-dot" style={{ animationDelay: '120ms' }} />
                                            <span className="w-1.5 h-1.5 rounded-full bg-violet-500 uatu-wave-dot" style={{ animationDelay: '240ms' }} />
                                            <span className="w-1.5 h-1.5 rounded-full bg-violet-500 uatu-wave-dot" style={{ animationDelay: '360ms' }} />
                                            <span className="w-1.5 h-1.5 rounded-full bg-violet-500 uatu-wave-dot" style={{ animationDelay: '480ms' }} />
                                        </div>
                                        <span className="text-xs text-slate-500 italic">{tenantAI.assistant_name} réfléchit…</span>
                                    </div>
                                </div>
                            );
                        })()}
                        </div>

                        {/* === Onglet Alertes — liste des observations proactives d'Uatu === */}
                        <div
                            className={`flex-1 overflow-y-auto bg-slate-50 ${activeTab === 'alerts' ? '' : 'hidden'}`}
                            data-testid="ai-chat-alerts-pane"
                        >
                            {allOpenObs.length === 0 ? (
                                <div className="h-full flex flex-col items-center justify-center px-6 py-10 text-center">
                                    <div className="w-14 h-14 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mb-3">
                                        <CheckCircle2 className="w-7 h-7 text-emerald-600" />
                                    </div>
                                    <p className="text-sm font-semibold text-slate-800">Tout est sous contrôle</p>
                                    <p className="text-xs text-slate-500 mt-1 max-w-[260px]">
                                        Je n&apos;ai rien de particulier à te signaler pour l&apos;instant. Je continue de surveiller en arrière-plan.
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => setActiveTab('conversation')}
                                        data-testid="ai-chat-alerts-empty-cta"
                                        className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-violet-700 hover:text-violet-900 border border-violet-300 hover:border-violet-500 bg-white hover:bg-violet-50 rounded-full px-4 py-2 transition-colors"
                                    >
                                        <MessageCircleMore className="w-3.5 h-3.5" />
                                        Discuter avec {tenantAI.assistant_name}
                                    </button>
                                </div>
                            ) : (
                                <div className="p-3 space-y-2.5">
                                    {allOpenObs.map((obs) => {
                                        const sev = obs.severity || 'vigilance';
                                        const sevStyle = sev === 'critical'
                                            ? { dot: 'bg-red-500', label: 'Critique', chip: 'bg-red-50 text-red-700 border-red-200' }
                                            : sev === 'important'
                                            ? { dot: 'bg-orange-500', label: 'Important', chip: 'bg-orange-50 text-orange-700 border-orange-200' }
                                            : { dot: 'bg-amber-400', label: 'Vigilance', chip: 'bg-amber-50 text-amber-800 border-amber-200' };
                                        return (
                                            <div
                                                key={obs.id}
                                                data-testid={`ai-chat-alert-${obs.id}`}
                                                className="rounded-xl bg-white border border-slate-200 p-3 hover:border-slate-300 transition-colors"
                                            >
                                                <div className="flex items-start gap-2">
                                                    <span className={`w-2 h-2 rounded-full ${sevStyle.dot} mt-1.5 shrink-0`} />
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-1.5 flex-wrap">
                                                            <h4 className="font-semibold text-slate-900 text-sm leading-tight flex-1 min-w-0">{obs.title}</h4>
                                                            <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${sevStyle.chip}`}>
                                                                {sevStyle.label}
                                                            </span>
                                                        </div>
                                                        {obs.message && (
                                                            <p className="text-xs text-slate-600 mt-1 leading-relaxed">{obs.message}</p>
                                                        )}
                                                        {/* Signaux (accordion) */}
                                                        {obs.signals && obs.signals.length > 0 && (
                                                            <details className="mt-2 group">
                                                                <summary className="cursor-pointer text-[11px] text-slate-500 hover:text-slate-700 list-none flex items-center gap-1">
                                                                    <span className="group-open:rotate-90 inline-block transition-transform">▸</span>
                                                                    Pourquoi Uatu me signale ça
                                                                </summary>
                                                                <ul className="mt-1 pl-3 space-y-0.5">
                                                                    {obs.signals.map((s, i) => (
                                                                        <li key={i} className="text-[11px] text-slate-600">
                                                                            <span className="font-medium">{s.label} :</span> {s.value}
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </details>
                                                        )}
                                                        {/* Actions */}
                                                        <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                                                            {(obs.actions || []).slice(0, 1).map((action, i) => (
                                                                <button
                                                                    key={i}
                                                                    type="button"
                                                                    onClick={() => openObservationAction(obs, action)}
                                                                    data-testid={`ai-chat-alert-action-${obs.id}-${i}`}
                                                                    className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full border border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100 px-2.5 py-1"
                                                                >
                                                                    {action.label}
                                                                </button>
                                                            ))}
                                                            <button
                                                                type="button"
                                                                onClick={() => talkAboutObservation(obs)}
                                                                data-testid={`ai-chat-alert-talk-${obs.id}`}
                                                                className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full border border-violet-300 bg-white text-violet-700 hover:bg-violet-50 px-2.5 py-1"
                                                                title={`Ouvrir la conversation à propos de cette alerte`}
                                                            >
                                                                <MessageCircleMore className="w-3 h-3" />
                                                                En parler
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => markObservationActed(obs)}
                                                                data-testid={`ai-chat-alert-done-${obs.id}`}
                                                                className="inline-flex items-center gap-1 text-[11px] text-emerald-700 hover:bg-emerald-50 px-2 py-1 rounded-full"
                                                                title="J'ai fait ce qu'il fallait"
                                                            >
                                                                <CheckCircle2 className="w-3 h-3" />
                                                                J&apos;ai fait
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => dismissObservation(obs)}
                                                                data-testid={`ai-chat-alert-dismiss-${obs.id}`}
                                                                className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:bg-slate-100 px-2 py-1 rounded-full ml-auto"
                                                                title="Ne plus me signaler ça"
                                                            >
                                                                <XIcon className="w-3 h-3" />
                                                                Écarter
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    <p className="text-[10px] text-slate-400 text-center pt-2 pb-1 italic">
                                        Uatu surveille en continu — cette liste se met à jour toute seule.
                                    </p>
                                    {/* Anti-oubli conversation — invite discrète à discuter librement */}
                                    <button
                                        type="button"
                                        onClick={() => setActiveTab('conversation')}
                                        data-testid="ai-chat-alerts-goto-conversation"
                                        className="w-full mt-1 py-2 px-3 rounded-lg bg-white border border-slate-200 hover:border-violet-300 hover:bg-violet-50 text-[11px] text-slate-600 hover:text-violet-700 font-medium flex items-center justify-center gap-1.5 transition-colors"
                                    >
                                        <MessageCircleMore className="w-3.5 h-3.5" />
                                        <span>Ou pose-moi une question directement</span>
                                        <span className="text-slate-400">→</span>
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Input + attachments — drop géré au niveau container parent */}
                        <div
                            className={`border-t border-slate-200 p-3 bg-white shrink-0 transition-colors ${dragActive ? 'bg-violet-50 ring-2 ring-violet-400 ring-inset' : ''}`}
                        >
                            {/* Liste des images actives de la session (Uatu Vision) */}
                            {sessionImages.length > 0 && (
                                <div className="mb-2 flex flex-wrap gap-1.5" data-testid="ai-chat-session-images">
                                    {sessionImages.map((img) => (
                                        <span
                                            key={img.id}
                                            className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-300 px-2.5 py-1 text-xs font-medium"
                                            data-testid={`ai-chat-image-${img.id}`}
                                            title="Uatu peut la lire à chaque tour de la session"
                                        >
                                            <span className="w-3 h-3 rounded-sm bg-emerald-600 inline-block" />
                                            <span className="max-w-[160px] truncate">{img.filename}</span>
                                            <span className="text-emerald-500">{Math.round((img.size_bytes || 0) / 1024)}Ko</span>
                                            <button
                                                type="button"
                                                onClick={() => removeSessionImage(img.id)}
                                                aria-label={`Retirer ${img.filename}`}
                                                className="ml-0.5 rounded-full hover:bg-emerald-200 p-0.5"
                                                data-testid={`ai-chat-image-remove-${img.id}`}
                                            >
                                                <XIcon className="w-3 h-3" />
                                            </button>
                                        </span>
                                    ))}
                                    {imageUploading && (
                                        <span className="inline-flex items-center gap-1.5 text-xs text-emerald-600">
                                            <Loader2 className="w-3 h-3 animate-spin" /> Upload image…
                                        </span>
                                    )}
                                </div>
                            )}

                            {/* Liste des PJ en attente */}
                            {attachments.length > 0 && (
                                <div className="mb-2 flex flex-wrap gap-1.5" data-testid="ai-chat-attachments">
                                    {attachments.map((a, idx) => (
                                        <span
                                            key={`${a.filename}-${idx}`}
                                            className="inline-flex items-center gap-1.5 rounded-full bg-violet-100 text-violet-800 border border-violet-300 px-2.5 py-1 text-xs font-medium"
                                            data-testid={`ai-chat-attachment-${idx}`}
                                        >
                                            <FileText className="w-3 h-3" />
                                            <span className="max-w-[160px] truncate">{a.filename}</span>
                                            <span className="text-violet-500">{a.chars}c{a.truncated ? '⤵' : ''}</span>
                                            <button
                                                type="button"
                                                onClick={() => removeAttachment(idx)}
                                                aria-label={`Retirer ${a.filename}`}
                                                className="ml-0.5 rounded-full hover:bg-violet-200 p-0.5"
                                                data-testid={`ai-chat-attachment-remove-${idx}`}
                                            >
                                                <XIcon className="w-3 h-3" />
                                            </button>
                                        </span>
                                    ))}
                                    {uploading && (
                                        <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
                                            <Loader2 className="w-3 h-3 animate-spin" /> Extraction…
                                        </span>
                                    )}
                                </div>
                            )}

                            {/* Layout responsive :
                                • Mobile (< sm) : textarea PLEINE LARGEUR (min 88 px)
                                  + rangée dédiée de gros boutons dessous → plus de
                                  tap accidentel sur Envoyer en visant la fin du texte.
                                • Desktop (≥ sm) : tout inline comme avant.  */}
                            <div className="flex flex-col sm:flex-row sm:items-end gap-2">
                                <textarea
                                    ref={(el) => {
                                        if (el) {
                                            // Auto-grow : ajuste la hauteur au contenu
                                            el.style.height = 'auto';
                                            el.style.height = Math.min(el.scrollHeight, 240) + 'px';
                                        }
                                    }}
                                    value={input}
                                    onChange={(e) => {
                                        setInput(e.target.value);
                                        // trigger height recompute
                                        e.target.style.height = 'auto';
                                        e.target.style.height = Math.min(e.target.scrollHeight, 240) + 'px';
                                    }}
                                    onKeyDown={onKeyDown}
                                    placeholder={listening ? 'Je t\'écoute…' : (dragActive ? 'Dépose PDF, texte ou image ici…' : (loading ? `${tenantAI.assistant_name} réfléchit — prépare ta prochaine question…` : `Pose ta question à ${tenantAI.assistant_name}…`))}
                                    rows={1}
                                    data-testid="ai-chat-input"
                                    className="w-full sm:flex-1 resize-none rounded-xl border border-slate-300 px-3 py-2.5 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent overflow-y-auto"
                                    style={{ minHeight: '88px', maxHeight: '240px' }}
                                />{/* Fix 22/02/2026 : plus de disabled={loading} — l'utilisateur peut préparer sa prochaine question pendant qu'Uatu répond. Seul l'ENVOI est bloqué (bouton disabled + Enter bloqué). */}
                                {/* Rangée boutons — pleine largeur mobile, inline desktop */}
                                <div className="flex items-center justify-between sm:justify-end gap-2">
                                    {/* Paperclip = attach PDF/TXT/MD/CSV */}
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".pdf,.txt,.md,.csv,application/pdf,text/plain,text/markdown,text/csv"
                                        onChange={async (e) => {
                                            const f = e.target.files?.[0];
                                            if (f) await uploadFile(f);
                                            if (e.target) e.target.value = '';
                                        }}
                                        className="hidden"
                                        data-testid="ai-chat-file-input"
                                    />
                                    {/* 📸 Image / Photo — bouton dédié (mobile camera + galerie) */}
                                    <input
                                        ref={imageInputRef}
                                        type="file"
                                        accept="image/*"
                                        onChange={async (e) => {
                                            const files = Array.from(e.target.files || []);
                                            for (const f of files) {
                                                // eslint-disable-next-line no-await-in-loop
                                                await uploadImage(f);
                                            }
                                            if (e.target) e.target.value = '';
                                        }}
                                        className="hidden"
                                        data-testid="ai-chat-image-input"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={loading || uploading}
                                        data-testid="ai-chat-attach-btn"
                                        className="rounded-xl p-3 sm:p-2.5 shrink-0 bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-40 transition-colors"
                                        aria-label="Joindre un fichier (PDF, TXT, MD, CSV)"
                                        title="Joindre un fichier · PDF, TXT, MD, CSV"
                                    >
                                        <Paperclip className="w-5 h-5 sm:w-4 sm:h-4" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => imageInputRef.current?.click()}
                                        disabled={loading || imageUploading}
                                        data-testid="ai-chat-attach-image-btn"
                                        className="rounded-xl p-3 sm:p-2.5 shrink-0 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-40 transition-colors"
                                        aria-label="Joindre une image ou capture d'écran"
                                        title="Photo, capture d'écran ou image (JPG, PNG, WEBP)"
                                    >
                                        <ImagePlus className="w-5 h-5 sm:w-4 sm:h-4" />
                                    </button>
                                    {voiceSupported && (
                                        <button
                                            type="button"
                                            onClick={toggleVoice}
                                            disabled={loading}
                                            data-testid="ai-chat-voice-btn"
                                            className={`rounded-xl p-3 sm:p-2.5 shrink-0 transition-all ${listening ? 'bg-red-500 text-white animate-pulse' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                                            aria-label={listening ? 'Arrêter le micro' : 'Parler à Uatu'}
                                            title={listening ? 'Arrêter' : 'Parler à Uatu'}
                                        >
                                            {listening ? <MicOff className="w-5 h-5 sm:w-4 sm:h-4" /> : <Mic className="w-5 h-5 sm:w-4 sm:h-4" />}
                                        </button>
                                    )}
                                    {/* 🛑 Bouton STOP pendant le stream · Envoyer sinon */}
                                    {loading ? (
                                        <button
                                            type="button"
                                            onClick={() => handleStop()}
                                            data-testid="ai-chat-stop-btn"
                                            className="rounded-xl bg-red-500 text-white px-5 py-3 sm:p-2.5 hover:bg-red-600 transition-colors shrink-0 flex items-center gap-2 font-semibold text-sm ml-auto sm:ml-0"
                                            aria-label="Arrêter"
                                            title="Arrêter la mission en cours"
                                        >
                                            <XIcon className="w-5 h-5 sm:w-4 sm:h-4" />
                                            <span className="sm:hidden">Stop</span>
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => sendMessage()}
                                            disabled={(!input.trim() && attachments.length === 0 && sessionImages.length === 0) || imageUploading || uploading}
                                            data-testid="ai-chat-send-btn"
                                            className="rounded-xl bg-violet-600 text-white px-5 py-3 sm:p-2.5 hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0 flex items-center gap-2 font-semibold text-sm ml-auto sm:ml-0"
                                            aria-label="Envoyer"
                                            title={imageUploading ? 'Upload image en cours — attends la fin' : (uploading ? 'Upload fichier en cours' : 'Envoyer')}
                                        >
                                            {(imageUploading || uploading) ? <Loader2 className="w-5 h-5 sm:w-4 sm:h-4 animate-spin" /> : <Send className="w-5 h-5 sm:w-4 sm:h-4" />}
                                            <span className="sm:hidden">{imageUploading ? 'Upload…' : 'Envoyer'}</span>
                                        </button>
                                    )}
                                </div>
                            </div>
                            <p className="text-[10px] text-slate-400 mt-1.5 text-center">
                                <span className="hidden sm:inline">Entrée pour envoyer · Shift+Entrée pour retour ligne · </span>
                                <span className="sm:hidden">Retour = nouvelle ligne · </span>
                                📎 Glisse ou joins un PDF/TXT · 📸 Photo dispo{input ? ' · Brouillon sauvegardé' : ''}{voiceSupported ? ' · 🎙 Micro dispo' : ''}
                            </p>
                        </div>
                    </div>
                </div>
            )}
            {/* 🧪 Modale Diagnostic Canary — admin_full only. Affiche le JSON
                retourné par /api/ai/canary-status. Utilise l'auth existante. */}
            {canaryDiag && (
                <div
                    className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
                    onClick={() => setCanaryDiag(null)}
                    data-testid="canary-diag-modal-backdrop"
                >
                    <div
                        className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-semibold text-slate-800">🧪 Diagnostic Canary — {window.location.hostname}</span>
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">admin_full</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    className="text-xs px-2 py-1 rounded bg-slate-200 hover:bg-slate-300 text-slate-700"
                                    onClick={() => {
                                        try {
                                            const text = canaryDiag === 'loading' ? '' : JSON.stringify(canaryDiag.ok ? canaryDiag.data : { error: canaryDiag.error }, null, 2);
                                            navigator.clipboard.writeText(text);
                                            toast.success('JSON copié');
                                        } catch { /* noop */ }
                                    }}
                                    data-testid="canary-diag-copy-btn"
                                >
                                    Copier JSON
                                </button>
                                <button
                                    type="button"
                                    className="text-xs px-2 py-1 rounded bg-slate-800 hover:bg-black text-white"
                                    onClick={() => setCanaryDiag(null)}
                                    data-testid="canary-diag-close-btn"
                                >
                                    Fermer
                                </button>
                            </div>
                        </div>
                        <div className="flex-1 overflow-auto p-4 bg-slate-900 text-slate-100 font-mono text-xs whitespace-pre-wrap" data-testid="canary-diag-body">
                            {canaryDiag === 'loading' ? (
                                <div className="flex items-center gap-2 text-slate-400">
                                    <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
                                </div>
                            ) : canaryDiag.ok ? (
                                JSON.stringify(canaryDiag.data, null, 2)
                            ) : (
                                <span className="text-red-400">Erreur : {String(canaryDiag.error)}</span>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
